import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

import { useTranslation } from 'providers/GILT.provider';
import { selectLoggedInStylistId } from 'selectors/selectLoggedInStylistId';
import fromAnyToTrimmed from 'utils/converters/any/fromAnyToTrimmed.util';
import { canManageOwnStaffStripe as canManageOwnStaffStripeUtil } from 'utils/stripe/canManageOwnStaffStripe.util';

/**
 * Hardcoded restriction (GS-5904 Phase 3): only the logged-in staff member
 * may set up or manage their own Stripe account.
 *
 * Returns:
 *   - canManageOwnStaffStripe(targetStylistId)
 *       Predicate. True only when the logged-in stylist matches the target.
 *   - blockIfNotOwn(targetStylistId, staffName?)
 *       Imperative guard for action handlers. Returns true when the action
 *       must be blocked (and arms the modal/message); false when allowed.
 *   - blockedMessage, isBlocked, clearBlocked
 *       State + setter for rendering the shared `StaffStripeBlockedModal`.
 *   - getBlockedMessage(staffName)
 *       Exposed for components that render the message inline (e.g. an
 *       AlertPanel inside an already-open modal) instead of the popup.
 */
export const useStaffStripeSelfManagement = () => {
  const { t } = useTranslation();
  const loggedInStylistId = useSelector(selectLoggedInStylistId);

  const [blockedMessage, setBlockedMessage] = useState('');

  const canManageOwnStaffStripe = useCallback((targetStylistId) => canManageOwnStaffStripeUtil(loggedInStylistId, targetStylistId), [loggedInStylistId]);

  const getBlockedMessage = useCallback(
    (staffName) =>
      t('stylistScreen.staffStripe.onlyStaffCanSetup', {
        staffName: fromAnyToTrimmed(staffName) || t('onlineBooking.generalSettings.staffOnlineServices.name'),
      }),
    [t],
  );

  const blockIfNotOwn = useCallback(
    (targetStylistId, staffName) => {
      if (canManageOwnStaffStripe(targetStylistId)) return false;
      setBlockedMessage(getBlockedMessage(staffName));
      return true;
    },
    [canManageOwnStaffStripe, getBlockedMessage],
  );

  const clearBlocked = useCallback(() => setBlockedMessage(''), []);

  return {
    canManageOwnStaffStripe,
    getBlockedMessage,
    blockIfNotOwn,
    blockedMessage,
    isBlocked: !!blockedMessage,
    clearBlocked,
  };
};
